import { ReactNode, useCallback, useEffect, useState } from "react";
import { AdminOrderContext } from "./AdminOrderContext";
import {
  AddressFormType,
  ClientDetails,
  Currency,
  Discount,
  OrderDetail,
} from "../../../types";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import OrderService from "../../../service/OrderService";
import TechpackService from "../../../service/TechpackService";
import { getReducedCartItems } from "../../../utils/CartUtils";
import { ROUTES } from "../../../constant/common.constant";

const AdminOrderProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [orderDetail, setOrderDetail] = useState<OrderDetail>();
  const [isOrderLoaded, setIsOrderLoaded] = useState(false);

  const [needOrderRefresh, setNeedOrderRefresh] = useState(true);

  useEffect(() => {
    if (!needOrderRefresh || !orderId) {
      return;
    }
    setIsOrderLoaded(false);
    OrderService.getPurchasedOrder(orderId).then((order) => {
      setOrderDetail(order.data.data);
      setIsOrderLoaded(true);
      setNeedOrderRefresh(false);
    });
  }, [orderId, needOrderRefresh]);

  const onGoToTechpack = useCallback(() => {
    navigate(
      generatePath(ROUTES.ADMIN_TECHPACK_ORDER, {
        orderId: orderId,
      })
    );
  }, [navigate, orderId]);

  const onEditItemDesign = useCallback(
    (designId: string) => {
      navigate(
        generatePath(ROUTES.ADMIN_STUDIO_PURCHASED_DESIGN, {
          designId: designId,
          orderType: "preorder",
          orderId: orderId,
          historyFrom: "adminpurchased",
        })
      );
    },
    [navigate, orderId]
  );

  const onUpdateSizesQty = useCallback(
    async (designId: string, sizes: any) => {
      const res = await OrderService.updatePurchasedItemSizes(
        orderId,
        designId,
        sizes
      );
      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onUpdatePoNumber = useCallback(
    async (poNumber: string) => {
      const res = await OrderService.updatePoNumber(orderId, poNumber);

      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onUpdateProjectNumber = useCallback(
    async (projectNumber: string) => {
      const data = {
        techpackId: orderDetail?.techpack?.id,
        projectNumber: projectNumber,
      };
      await TechpackService.setProjectNumber(data);
      setOrderDetail((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          techpack: { ...prev.techpack, projectNumber: projectNumber },
        };
      });
    },
    [orderDetail?.techpack]
  );

  const onUpdateCarbonNeutral = useCallback(
    async (carbonNeutral: boolean) => {
      const data = {
        preorderId: orderId,
        carbonNeutral: carbonNeutral,
      };
      const res = await OrderService.setCarbonNeutral(data);

      setOrderDetail(res.data.data);
    },
    [orderId]
  );

  const onUpdateCurrency = useCallback(
    async (currency: Currency) => {
      const res = await OrderService.updateCurrency(orderId, currency);
      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onUpdateShippingAddress = useCallback(
    async (shippingAddress: AddressFormType) => {
      const currency = orderDetail?.pricing.currency;
      const res = await OrderService.updateShippingAddressAndCurrency(
        orderId,
        shippingAddress,
        currency
      );
      setOrderDetail(res.data.data);
    },
    [orderId, orderDetail?.pricing.currency, setOrderDetail]
  );

  const onUpdateBillingAddress = useCallback(
    async (billingAddress: AddressFormType) => {
      const res = await OrderService.updateBillingAddress(
        orderId,
        billingAddress
      );
      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onUpdateDiscounts = useCallback(
    async (discounts: Discount[]) => {
      const res = await OrderService.upsertDiscounts(orderId, discounts);
      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onRemoveDesign = useCallback(
    async (designId: string) => {
      const res = await OrderService.removeDesign(orderId, designId);
      setOrderDetail(res.data.data);
    },
    [orderId, setOrderDetail]
  );

  const onAddNewDesign = useCallback(
    async (designId: string, sizes: any) => {
      const res = await OrderService.addDesign(orderId, designId, sizes);
      setOrderDetail(res.data.data);

      return { orderDetail: res.data.data, error: {} };
    },
    [orderId]
  );

  const onUpdateClientDetailsField = useCallback(
    async (field: keyof ClientDetails, value: string) => {
      const res = await OrderService.updateClientDetails(orderId, {
        ...orderDetail?.clientDetails,
        [field]: value,
      });
      setOrderDetail(res.data.data);
    },
    [orderId, orderDetail?.clientDetails]
  );

  return (
    <AdminOrderContext.Provider
      value={{
        orderDetail: orderDetail,
        reducedOrderItems: getReducedCartItems(
          orderDetail?.pricing.comboItems ?? []
        ),
        onEditItemDesign,
        onAddNewDesign,
        onGoToTechpack,
        onUpdateSizesQty,
        onUpdateProjectNumber,
        onUpdateCarbonNeutral,
        onUpdatePoNumber,
        onUpdateShippingAddress,
        onUpdateClientDetailsField,
        onUpdateBillingAddress,
        onUpdateDiscounts,
        onUpdateCurrency,
        onRemoveDesign,
        isOrderLoaded,
      }}
    >
      {children}
    </AdminOrderContext.Provider>
  );
};

export default AdminOrderProvider;
